import React from 'react';
import useDarkMode from '../../../hooks/useDarkMode';
import { useEffect } from 'react';
import Logo from '../../../components/Logomenu';
import '../../../pages/presentation/auth/Login.css';

const Terms = () => {
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();

	useEffect(() => {
		const storedTheme = localStorage.getItem('theme');
		if (storedTheme !== null) {
			setDarkModeStatus(storedTheme === 'dark');
		}
	}, [setDarkModeStatus]);

	const items = [
		{
			title: 'Service Description',
			content: (
				<p className='text-justify'>
					SchedulerDesk acts as your go-to online platform for effortlessly booking
					appointments at a variety of salons and spas. Please note that while we
					facilitate these bookings, we do not own or manage any of the businesses listed
					on our platform.
				</p>
			),
		},
		{
			title: 'User Accounts',
			content: (
				<p className='text-justify'>
					Accessing SchedulerDesk requires creating an account. It's crucial to safeguard
					your account details and usage, including login credentials. You are solely
					responsible for all activities undertaken through your account.
				</p>
			),
		},
		{
			title: 'Appointment Bookings',
			content: (
				<p className='text-justify'>
					Whether it's a haircut, massage, or manicure, SchedulerDesk is here to help you
					schedule your desired treatments. Accuracy in providing information during
					booking is key, and remember to cancel any appointments you can't make. Some
					venues may require deposits or prepayments for specific services.
				</p>
			),
		},
		{
			title: 'Payment',
			content: (
				<p className='text-justify'>
					We're proud to offer SchedulerDesk's services to you free of charge. However,
					please be aware that certain establishments may request deposits or prepayments
					for select services. Any payment-related disputes should be handled directly
					with the salon in question.
				</p>
			),
		},
		{
			title: 'Cancellations and Refunds',
			content: (
				<p className='text-justify'>
					Should you need to cancel an appointment, please adhere to the salon's
					cancellation policy. While some venues may offer refunds or rescheduling options
					for canceled appointments, others may not. SchedulerDesk assumes no
					responsibility for refund requests or cancellations.
				</p>
			),
		},
		{
			title: 'Reviews and Ratings',
			content: (
				<p className='text-justify mb-4'>
					Your feedback matters! Feel free to leave honest and accurate reviews and
					ratings for salons and spas you've visited through SchedulerDesk. We reserve the
					right to remove any reviews or ratings deemed fraudulent or violating our
					content guidelines.
				</p>
			),
		},
		{
			title: 'Prohibited Conduct',
			content: (
				<p className='text-justify mb-4'>
					Please refrain from engaging in any illegal activities or behaviors that violate
					our terms of service while using SchedulerDesk. This includes impersonation,
					spamming, harassment, or any attempts to compromise the integrity of our system.
				</p>
			),
		},
		{
			title: 'Termination',
			content: (
				<p className='text-justify mb-4'>
					We reserve the right to terminate your account and access to SchedulerDesk at
					our discretion, without prior notice. Additionally, we may modify or discontinue
					our service without prior notification.
				</p>
			),
		},
		{
			title: 'Limitation of Liability',
			content: (
				<p className='text-justify mb-4'>
					While we strive to provide you with a seamless experience, SchedulerDesk cannot
					be held liable for any damages or losses resulting from your use of our service.
					This includes direct, indirect, incidental, consequential, or punitive damages,
					with our liability capped at the amount paid for our service.
				</p>
			),
		},
		{
			title: 'Governing Law',
			content: (
				<p className='text-justify mb-4'>
					These terms of service are governed by the laws of the jurisdiction where
					SchedulerDesk operates. Any legal actions related to these terms must be pursued
					in the appropriate court within that jurisdiction.
				</p>
			),
		},
	];

	const cardBg = darkModeStatus ? '#23263a' : '#ffffff';
	const pageBg = darkModeStatus ? 'transparent' : '#f5f7ff';
	const textColor = darkModeStatus ? '#e7eef8' : '#23263a';
	const subtleText = darkModeStatus ? 'rgba(231, 238, 248, 0.72)' : '#6b7280';
	const borderColor = darkModeStatus ? '#35395a' : '#e9edf5';
	const accent = '#4d69fa';
	const paragraphStyle = { textAlign: 'justify', color: subtleText, marginBottom: 0 };
	const titleStyle = { color: textColor, fontWeight: 800, marginBottom: 8 };
	const subTitleStyle = { color: subtleText, marginBottom: 18 };
	const bulletTitleStyle = { color: textColor, fontWeight: 700, marginBottom: 6 };
	const cardStyle = {
		width: '100%',
		maxWidth: '90%',
		borderRadius: 16,
		border: `1px solid ${borderColor}`,
		background: cardBg,
		boxShadow: darkModeStatus
			? '0 14px 40px rgba(0,0,0,0.35)'
			: '0 14px 40px rgba(77,105,250,0.12)',
		overflow: 'hidden',
	};
	return (
		<div className='bg-img'>
			<div
				style={{
					minHeight: '100vh',
					width: '100%',
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'center',
					padding: '32px 0',
					overflowY: 'auto',
				}}>
				<div style={cardStyle}>
					<div
						style={{
							padding: 18,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							borderBottom: `1px solid ${borderColor}`,
							background: darkModeStatus ? '#181a25' : '#f8faff',
						}}>
						<Logo />
					</div>

					<div style={{ padding: 24 }}>
						<div style={{ marginBottom: 18 }}>
							<h2 style={titleStyle}>Terms of Service</h2>
							<div style={subTitleStyle}>
								Welcome to SchedulerDesk. Please review these terms before using the
								platform.
							</div>
							<div style={{ fontSize: 13, color: subtleText }}>
								Updated May 20, 2024
							</div>
						</div>

						<div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: 18 }}>
							{items.map((item, idx) => (
								<div
									key={`${item.title}-${idx}`}
									style={{
										padding: '14px 12px',
										borderRadius: 12,
										border: `1px solid ${borderColor}`,
										background: darkModeStatus ? '#181a25' : '#f8faff',
										marginBottom: 12,
									}}>
									<div
										style={{
											display: 'flex',
											gap: 10,
											alignItems: 'flex-start',
										}}>
										<div
											style={{
												color: accent,
												fontWeight: 900,
												lineHeight: '24px',
											}}>
											●
										</div>
										<div style={{ flex: 1 }}>
											<div style={bulletTitleStyle}>{item.title}</div>
											<div style={paragraphStyle}>{item.content}</div>
										</div>
									</div>
								</div>
							))}
						</div>

						<div
							style={{
								borderTop: `1px solid ${borderColor}`,
								marginTop: 18,
								paddingTop: 18,
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: 12,
								flexWrap: 'wrap',
							}}>
							<div style={{ color: subtleText, fontSize: 14 }}>
								Questions? Email{' '}
								<a
									href='mailto:support@schedulerdesk.com'
									style={{ color: accent, fontWeight: 700 }}>
									support@schedulerdesk.com
								</a>
							</div>
							{/* <Link
							to='/'
							className='onboarding-btn'
							style={{ textDecoration: 'none', minWidth: 180, textAlign: 'center' }}>
							Back to Setup
						</Link> */}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Terms;
